import { useState } from "react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Badge } from "../../components/shared/Badge";
import { useAuditLog } from "@platform/hooks";

interface AuditEntry {
  id: string;
  action: string;
  actorEmail: string | null;
  enterpriseName: string | null;
  targetLabel: string | null;
  targetType: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

const ACTION_VARIANTS: Record<string, "green" | "red" | "blue" | "amber" | "purple" | "gray"> = {
  USER_CREATED: "green",
  USER_APPROVED: "green",
  USER_DECLINED: "red",
  USER_SUSPENDED: "amber",
  USER_DELETED: "red",
  PASSWORD_RESET: "amber",
  ROLE_CREATED: "blue",
  ROLE_UPDATED: "blue",
  ROLE_DELETED: "red",
  ROLE_ASSIGNED: "blue",
  ROLE_REVOKED: "amber",
  ENTERPRISE_CREATED: "green",
  ENTERPRISE_SUSPENDED: "amber",
  ENTERPRISE_REINSTATED: "green",
  MODULE_ENABLED: "green",
  MODULE_DISABLED: "amber",
  DEFAULT_ENTERPRISE_CHANGED: "blue",
  LOGIN: "gray",
  LOGOUT: "gray",
  APP_SETTINGS_UPDATED: "blue",
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return new Date(iso).toLocaleDateString();
}

export function PlatformAuditLogPage() {
  const { data: logs = [], isLoading } = useAuditLog();
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("All");

  const typedLogs = logs as AuditEntry[];

  const actionTypes = ["All", ...Array.from(new Set(typedLogs.map((l) => l.action))).sort()];

  const filtered = typedLogs.filter((l) => {
    const matchSearch =
      !search ||
      l.actorEmail?.toLowerCase().includes(search.toLowerCase()) ||
      l.enterpriseName?.toLowerCase().includes(search.toLowerCase()) ||
      l.targetLabel?.toLowerCase().includes(search.toLowerCase()) ||
      l.action.toLowerCase().includes(search.toLowerCase());
    const matchAction = actionFilter === "All" || l.action === actionFilter;
    return matchSearch && matchAction;
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Audit log"
        subtitle="Immutable record of all platform-level events"
      />

      <div className="flex gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by actor, enterprise, or target..."
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 w-72"
        />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
        >
          {actionTypes.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-ds-border rounded-card shadow-card overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">Loading audit log...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-ds-text-muted">No events found.</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ds-border bg-ds-bg">
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3 w-44">Event</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Actor</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Enterprise</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3">Target</th>
                <th className="text-left text-xs font-medium text-ds-text-muted uppercase tracking-wide px-5 py-3 w-24">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-border">
              {filtered.map((l) => (
                <tr key={l.id} className="hover:bg-ds-bg/50">
                  <td className="px-5 py-3">
                    <Badge variant={ACTION_VARIANTS[l.action] ?? "gray"}>
                      {l.action.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-xs text-ds-text-muted">
                    {l.actorEmail ?? "System"}
                  </td>
                  <td className="px-5 py-3 text-xs text-ds-text">
                    {l.enterpriseName ?? "—"}
                  </td>
                  <td className="px-5 py-3 text-xs text-ds-text-muted">
                    {l.targetLabel ? (
                      <>
                        <span className="text-ds-text-muted">{l.targetType} · </span>
                        {l.targetLabel}
                      </>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3 text-xs text-ds-text-muted whitespace-nowrap">
                    {timeAgo(l.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!isLoading && (
        <p className="text-xs text-ds-text-muted">
          Showing {filtered.length} of {typedLogs.length} events (last 100 entries)
        </p>
      )}
    </div>
  );
}
